package mapsearch.generalMarkers

import apiclient.FormationClient
import apiclient.geoobjects.GeoObjectDetails
import apiclient.geoobjects.ObjectTags
import apiclient.geoobjects.ObjectType
import apiclient.geoobjects.SearchQueryContext
import apiclient.geoobjects.connectableGeometry
import apiclient.geoobjects.connectableObjectExclusionZoneGeometry
import apiclient.geoobjects.newContext
import apiclient.geoobjects.restSearch
import apiclient.tags.tag
import auth.WorkspacesStore
import com.jillesvangurp.geo.GeoGeometry
import com.jillesvangurp.geojson.PolygonCoordinates
import dev.fritz2.core.RootStore
import dev.fritz2.core.storeOf
import koin.koinCtx
import koin.withKoin
import kotlin.time.measureTime
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.combine
import mainmenu.AppStateStore
import maplibreGL.ConnectableAndPosition
import model.AppPhase

class GeneralMarkerLookUpStore : RootStore<Map<String, GeoObjectDetails>>(emptyMap(), Job()) {

    val workspacesStore: WorkspacesStore by koinCtx.inject()
    val generalMarkersTilesLookUpStore: GeneralMarkersTilesLookUpStore by koinCtx.inject()
    val appStateStore: AppStateStore by koinCtx.inject()

    var fetching = storeOf(false, job)

    var connectableShapesCacheMap: MutableMap<String, ConnectableAndPosition> = mutableMapOf()


    suspend fun fetchAllGeneralMarkers(workspaceIds: List<String>? = null) {
        if (!fetching.current) {
            try {
                fetching.update(true)
                update(emptyMap())
                withKoin {
                    val client = get<FormationClient>()
                    (workspaceIds ?: workspacesStore.current.map { it.groupId }).let { groupIds ->
                        client.restSearch(
                            SearchQueryContext.newContext(groupIds).copy(
                                objectTypes = listOf(ObjectType.GeneralMarker),
                                excludeTags = listOfNotNull(
                                    ObjectTags.Deleted.tag(true),
                                    ObjectTags.Archived.tag(true),
                                    ObjectTags.IsArchetype.tag(true),
                                ),
                                size = 1000,
                            ),
                        ).fold(
                            { results ->
                                results.hits.map { it.hit }.associateBy { it.id }.let { resMap ->
                                    update(resMap)
                                    generalMarkersTilesLookUpStore.add(resMap.mapValues { it.value.latLon })
                                    connectableShapesCacheMap = generateConnectableGeoShapesMap(resMap).toMutableMap()
                                }
                            },
                            { error ->
                                console.error("Failed to fetch general markers.", error.message, error.cause)
                            },
                        )
                    }
                }
            } finally {
                fetching.update(false)
            }
        }
    }

    fun upsertGeneralMarker(marker: GeoObjectDetails) {
        upsertGeneralMarkers(mapOf(marker.id to marker))
    }

    val upsertGeneralMarkers = handle<Map<String, GeoObjectDetails>> { current, newGeneralMarkers ->
        val mutable = current.toMutableMap()
        newGeneralMarkers.entries.forEach { (generalMarkerId, generalMarker) ->
            generalMarkersTilesLookUpStore.updateGeneralMarkerInTileLookUp(generalMarker.id, generalMarker.latLon)
            mutable[generalMarkerId] = generalMarker
        }
        connectableShapesCacheMap = generateConnectableGeoShapesMap(mutable).toMutableMap()
        mutable.toMap()
    }

    fun removeGeneralMarker(generalMarker: GeoObjectDetails) {
        removeGeneralMarkers(listOf(generalMarker))
    }

    val removeGeneralMarkers = handle<List<GeoObjectDetails>> { current, generalMarkers ->
        generalMarkersTilesLookUpStore.remove(generalMarkers.associate { it.id to it.latLon })
        current - generalMarkers.map { it.id }
    }

    private fun generateConnectableGeoShapesMap(objectsMap: Map<String, GeoObjectDetails>): Map<String, ConnectableAndPosition> {
        return objectsMap.mapNotNull { (id, geoObjectDetails) ->
            geoObjectDetails.geoReferencedConnectableObject?.let { connectableShape ->
                id to ConnectableAndPosition(connectableShape, geoObjectDetails.latLon)
            }
        }.toMap()
    }

    fun getConnectableGeoShapesMap(currentActiveObj: GeoObjectDetails): Map<String, ConnectableAndPosition> {
        val tempShapesMap = connectableShapesCacheMap
        currentActiveObj.geoReferencedConnectableObject?.let { connectableShape ->
            tempShapesMap[currentActiveObj.id] = ConnectableAndPosition(connectableShape, currentActiveObj.latLon)
        }
        return tempShapesMap
    }

    fun getOverlappingGeoShapeIds(currentActiveObj: GeoObjectDetails): Set<String> {
        val updatedMap = current + mapOf(currentActiveObj.id to currentActiveObj)
        val cache = mutableMapOf<String, PolygonCoordinates?>()

        var ret = emptySet<String>()
        measureTime {
            ret = updatedMap.asSequence()
                .filter { (selfId, selfObj) ->
                    val shapeCoordinates = cache.getOrPut(selfId) { selfObj.connectableGeometry()?.coordinates }

                    updatedMap.any { (otherId, otherObj) ->
                        if (selfId == otherId) return@any false

                        val otherCoordinates = cache.getOrPut(otherId) { otherObj.connectableGeometry()?.coordinates }
                        val otherExclusionZoneCoordinates = cache.getOrPut("${otherId}_exclusion") {
                            otherObj.geoReferencedConnectableObject?.connectableObjectExclusionZoneGeometry(otherObj.latLon)?.coordinates
                        }

                        shapeCoordinates != null && otherCoordinates != null &&
                            (GeoGeometry.overlap(otherCoordinates, shapeCoordinates) ||
                                (otherExclusionZoneCoordinates?.let { GeoGeometry.overlap(it, shapeCoordinates) } ?: false))
                    }
                }
                .map { it.key }
                .toSet()
        }.also { time ->
            console.info(" Time: getOverlappingGeoShapeIds took ${time.inWholeMilliseconds}ms")
        }
        return ret
    }

    init {
        combine(appStateStore.data, workspacesStore.data) { a, w -> Pair(a.appPhase, w) } handledBy { (appPhase, workspaces) ->
            if (appPhase == AppPhase.LoggedInAnonymous || appPhase == AppPhase.LoggedIn) {
                workspaces.mapNotNull { it.groupId.ifBlank { null } }
                    .ifEmpty { null }?.let { workspaceIds ->
                        fetchAllGeneralMarkers(workspaceIds)
                    }
            }
        }
    }

}
